// Serie semanal de colocación del vendedor para la tarjeta de Tendencia. Puro.
import type { BackofficeSale } from '@/api/services/retail-wizard/contract';
import { soldCentsOf } from '@/api/services/retail-wizard/soldAmount';
import { dayOffset, mxDay } from '@/lib/dateHelpers';
import { trendChangePct } from '../../tiendas/lib/storeDetail';
import { TREND_WEEK_DAYS, TREND_WEEKS } from '../constants';

type TrendSale = Pick<
    BackofficeSale,
    'soldAt' | 'cancelledAt' | 'amountCents' | 'equipmentPriceCents' | 'soldCents'
>;

const DAY_MS = 86_400_000;

/** Días de calendario entre dos `YYYY-MM-DD`, sin depender de la zona del navegador. */
function calendarDaysBetween(day: string, fromDay: string): number {
    return Math.round(
        (Date.parse(`${day}T00:00:00Z`) - Date.parse(`${fromDay}T00:00:00Z`)) / DAY_MS,
    );
}

/** Primer y último día (CDMX) de las últimas `weeks` semanas, hasta hoy. */
export function trendDays(now: Date, weeks: number = TREND_WEEKS): { from: string; to: string } {
    const to = mxDay(now.toISOString());
    return { from: dayOffset(to, -(weeks * TREND_WEEK_DAYS - 1)), to };
}

export interface TrendWeek {
    fromDay: string;
    toDay: string;
    placedCents: number;
}

/** Colocación por semana de 7 días que cierra hoy (CDMX), de la más vieja a la más nueva; sin canceladas. */
export function weeklyPlacedSeries(
    sales: readonly TrendSale[],
    now: Date,
    weeks: number = TREND_WEEKS,
): TrendWeek[] {
    const today = mxDay(now.toISOString());
    const series = Array.from({ length: weeks }, (_, index) => {
        const toDay = dayOffset(today, -TREND_WEEK_DAYS * (weeks - 1 - index));
        return { fromDay: dayOffset(toDay, -(TREND_WEEK_DAYS - 1)), toDay, placedCents: 0 };
    });
    for (const sale of sales) {
        if (sale.cancelledAt !== null) continue;
        const ago = calendarDaysBetween(today, mxDay(sale.soldAt));
        const back = Math.floor(ago / TREND_WEEK_DAYS);
        if (ago < 0 || back >= weeks) continue;
        series[weeks - 1 - back].placedCents += soldCentsOf(sale);
    }
    return series;
}

export type TrendTone = 'up' | 'down' | 'flat';

export interface TrendDelta {
    pct: number | null;
    tone: TrendTone;
    label: string;
}

/** Cambio de la última semana contra la primera de la serie; «—» si la primera es 0. */
export function trendDelta(series: readonly number[]): TrendDelta {
    const pct = trendChangePct(series);
    if (pct === null) return { pct, tone: 'flat', label: '—' };
    const tone: TrendTone = pct > 0 ? 'up' : pct < 0 ? 'down' : 'flat';
    return { pct, tone, label: `${pct > 0 ? '+' : ''}${String(pct)}%` };
}
